import React from 'react';
import { Card, CardContent, Typography, Chip, Box } from '@mui/material';
import {
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { MarketplaceItem } from '../../types/marketplace';

interface MarketplaceItemCardProps {
  item: MarketplaceItem;
  onClick: () => void;
}

const MarketplaceItemCard: React.FC<MarketplaceItemCardProps> = ({
  item,
  onClick,
}) => {
  const formatPrice = (price: string) => {
    if (price.startsWith('$')) return price;
    // Check if price starts with a number
    if (/^\d/.test(price)) {
      return `$${price}`;
    }
    // For non-numeric prices like "Varies", "Free", etc., return as-is
    return price;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'sold':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get primary image or first image as fallback
  const getPrimaryImage = () => {
    if (!item.images || item.images.length === 0) {
      return 'https://via.placeholder.com/400x300/CCCCCC/666666?text=No+Image';
    }

    // Find primary image first
    const primaryImage = item.images.find((img) => img.is_primary);
    if (primaryImage) {
      return primaryImage.url;
    }

    // Fallback to first image
    return (
      item.images[0]?.url ||
      'https://via.placeholder.com/400x300/CCCCCC/666666?text=No+Image'
    );
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          height: { xs: '150px', sm: '180px', md: '200px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          overflow: 'hidden',
        }}
      >
        <img
          src={getPrimaryImage()}
          alt={item.name}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            width: 'auto',
            height: 'auto',
          }}
        />
      </Box>
      <CardContent
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ mb: 1 }}>
          <Chip
            label={item.status}
            size="small"
            color={getStatusColor(item.status) as any}
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Typography variant="h6" component="h3">
            {item.name}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 2,
            flexGrow: 1,
          }}
        >
          {item.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <MoneyIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="h6" color="primary" fontWeight="bold">
            {formatPrice(item.price)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LocationIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {item.local_chapter}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MarketplaceItemCard;
